import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Copy, Check, UserPlus, Plus, Save, FlaskConical, Lock, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useDemo } from "@/components/demo/DemoUi";
import { useLang } from "@/i18n/LanguageContext";
import BillingPanel from "@/components/BillingPanel";
import { CountrySelect } from "@/components/CountrySelect";
import { MarketSelect } from "@/components/MarketSelect";
import { objectiveLabel } from "@/lib/profileOptions";
import { continentForCountryName } from "@/lib/continents";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const ROLES = ["Administrator", "Analyst", "Member", "Viewer"];
const RANK = { Owner: 5, Administrator: 4, Analyst: 3, Member: 2, Viewer: 1 };

const PERIODS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const OBJECTIVES = [
  "Increase revenue", "Develop low season", "Find new source markets", "Improve occupancy",
  "Develop distribution", "Activate partners", "Improve visitor dispersion", "Measure performance",
];

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all ${
      active ? "border-[#1E3D2B] bg-[#1E3D2B] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#CBD5E1]"
    }`}
  >
    {active && <Check size={12} className="mr-1 inline" />}
    {children}
  </button>
);

const roleStyle = {
  Owner: "bg-[#1E3D2B] text-white",
  Administrator: "bg-[#ECF3EC] text-[#3E7256]",
  Analyst: "bg-[#ECFDF5] text-[#059669]",
  Member: "bg-slate-100 text-slate-600",
  Viewer: "bg-[#FFFBEB] text-[#D97706]",
};

const CopyField = ({ value, testid }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <input
        readOnly
        value={value}
        data-testid={testid}
        className="h-9 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 font-mono text-xs text-slate-600"
      />
      <button
        onClick={() => {
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
      >
        {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
      </button>
    </div>
  );
};

export default function Settings() {
  const { activeMembership, activeOrgId, reload } = useAuth();
  const { readOnly } = useDemo();
  const { lang } = useLang();
  const navigate = useNavigate();
  // Immutable demo + non-admin (Viewer/Member/Analyst): serve a sanitized read-only page that never
  // exposes member names/emails, invitations, billing identifiers or any mutation control.
  const demoViewer = readOnly && activeMembership && RANK[activeMembership.role] < RANK.Administrator;
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [records, setRecords] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Member");
  const [newLink, setNewLink] = useState(null);
  const [newRecord, setNewRecord] = useState("");

  const org = activeMembership?.org;
  const marketsKey = (org?.source_markets || []).join(",");
  const periodsKey = (org?.priority_periods || []).join(",");
  const objectivesKey = (org?.objectives || []).join(",");
  const [profile, setProfile] = useState({
    country: "", region: "", geographic_level: "", geographic_entity: "", geographic_code: "",
    source_markets: [], priority_periods: [], objectives: [],
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (org) {
      setProfile({
        country: org.country || "",
        region: org.region || "",
        geographic_level: org.geographic_level || "",
        geographic_entity: org.geographic_entity || "",
        geographic_code: org.geographic_code || "",
        source_markets: org.source_markets || [],
        priority_periods: org.priority_periods || [],
        objectives: org.objectives || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id, org?.country, org?.region, org?.geographic_level, org?.geographic_entity, marketsKey, periodsKey, objectivesKey]);

  const toggleProfile = (key, value) =>
    setProfile((p) => ({
      ...p,
      [key]: p[key].includes(value) ? p[key].filter((v) => v !== value) : [...p[key], value],
    }));

  const canManage = activeMembership && RANK[activeMembership.role] >= RANK.Administrator && !readOnly;
  const canWrite = activeMembership && RANK[activeMembership.role] >= RANK.Member && !readOnly;

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      // Never send empty strings (aligns with onboarding 3A.1). Optional/text fields are omitted
      // when blank so a scopeless org can save without tripping geo-scope validation.
      const payload = {
        source_markets: profile.source_markets,
        priority_periods: profile.priority_periods,
        objectives: profile.objectives,
      };
      for (const f of ["country", "region", "geographic_level", "geographic_entity", "geographic_code"]) {
        const v = (profile[f] || "").trim();
        if (v) payload[f] = v;
      }
      await api.patch(`/organizations/${activeOrgId}/profile`, payload);
      await reload();
      toast.success("Profil de l'organisation mis à jour");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setSavingProfile(false);
    }
  };

  const load = useCallback(async () => {
    if (!activeOrgId || demoViewer) return;
    try {
      const [mRes, rRes] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/members`),
        api.get(`/organizations/${activeOrgId}/records`),
      ]);
      setMembers(mRes.data.members);
      setRecords(rRes.data.records);
      if (canManage) {
        const iRes = await api.get(`/organizations/${activeOrgId}/invitations`);
        setInvitations(iRes.data.invitations);
      } else {
        setInvitations([]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrgId, canManage, demoViewer]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setNewLink(null);
    try {
      const { data } = await api.post(`/organizations/${activeOrgId}/invitations`, {
        email: inviteEmail,
        role: inviteRole,
      });
      setNewLink(data.invite_link);
      setInviteEmail("");
      toast.success("Invitation générée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    }
  };

  const addRecord = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/organizations/${activeOrgId}/records`, { title: newRecord });
      setNewRecord("");
      toast.success("Donnée ajoutée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    }
  };

  if (demoViewer) {
    const destination = org?.geographic_entity || org?.region || org?.country || "Maurice";
    const langLabel = lang === "en" ? "English" : "Français";
    const T = lang === "en"
      ? {
          title: "Demonstration settings",
          subtitle: "Read-only demonstration workspace. Member and billing details are hidden.",
          destination: "Destination",
          language: "Language",
          status: "Status",
          statusValue: "Immutable demo · Read-only",
          note: "This is a guided demonstration. Organisation members, invitations and billing are not available here. All changes are disabled.",
          back: "Back to Dashboard",
        }
      : {
          title: "Paramètres de démonstration",
          subtitle: "Espace de démonstration en lecture seule. Les informations des membres et de facturation sont masquées.",
          destination: "Destination",
          language: "Langue",
          status: "Statut",
          statusValue: "Démo immuable · Lecture seule",
          note: "Ceci est une démonstration guidée. Les membres de l'organisation, les invitations et la facturation ne sont pas accessibles ici. Toute modification est désactivée.",
          back: "Retour au Dashboard",
        };
    return (
      <div className="space-y-8" data-testid="settings-demo-page">
        <div>
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 w-fit" data-testid="settings-demo-banner">
            <FlaskConical size={16} className="text-amber-600" />
            <span className="text-xs font-semibold text-amber-900">{T.statusValue}</span>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#1E3D2B] sm:text-3xl">{T.title}</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">{T.subtitle}</p>
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="settings-demo-summary">
          <dl className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{T.destination}</dt>
              <dd className="mt-1.5 text-sm font-medium text-[#1E3D2B]" data-testid="settings-demo-destination">{destination}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{T.language}</dt>
              <dd className="mt-1.5 text-sm font-medium text-[#1E3D2B]" data-testid="settings-demo-language">{langLabel}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{T.status}</dt>
              <dd className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-medium text-[#B45309]" data-testid="settings-demo-status">
                <Lock size={13} /> {T.statusValue}
              </dd>
            </div>
          </dl>
          <p className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="settings-demo-note">{T.note}</p>
          <button
            type="button"
            data-testid="settings-demo-back"
            onClick={() => navigate("/dashboard")}
            className="mt-6 inline-flex h-11 items-center gap-2 rounded-lg bg-[#1E3D2B] px-6 text-sm font-semibold text-white hover:bg-[#2C5540]"
          >
            <ArrowLeft size={16} /> {T.back}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-[#1E3D2B] sm:text-3xl">
          Paramètres de l'organisation
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Membres, invitations et données de{" "}
          <span className="font-semibold text-slate-700">{activeMembership?.org.name}</span>.
        </p>
      </div>

      {/* Organization profile (editable) */}
      <section className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="org-profile-section">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-semibold text-[#1E3D2B]">Profil de l'organisation</h2>
            <p className="mt-1 text-xs text-slate-400">Pays, marchés, périodes prioritaires et objectifs.</p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">{org?.type}</span>
        </div>

        <form onSubmit={saveProfile} className="mt-6 space-y-7" data-testid="org-profile-form">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pays</label>
              {canManage ? (
                <CountrySelect
                  value={profile.country}
                  onChange={(v) => setProfile((p) => ({ ...p, country: v }))}
                  testid="profile-country"
                />
              ) : (
                <p className="text-sm text-slate-700" data-testid="profile-country-ro">{profile.country || "—"}</p>
              )}
              {continentForCountryName(profile.country) && (
                <p className="text-xs text-slate-400" data-testid="profile-continent">
                  Continent : <span className="font-medium text-slate-600">{continentForCountryName(profile.country)}</span>
                  <span className="ml-1 text-slate-400">(déduit du pays)</span>
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Destination représentée (optionnel)</label>
              {canManage ? (
                <input
                  value={profile.region}
                  onChange={(e) => setProfile((p) => ({ ...p, region: e.target.value }))}
                  data-testid="profile-region"
                  placeholder="ex. Côte d'Azur, Savoie Mont-Blanc"
                  className="h-11 w-full rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
                />
              ) : (
                <p className="text-sm text-slate-700">{profile.region || "—"}</p>
              )}
              <p className="text-xs text-slate-400">
                Nom usuel de la destination ou zone touristique représentée par votre organisation
                (ex. Côte d'Azur, Savoie Mont-Blanc). Champ libre, indépendant du pays.
              </p>
            </div>
          </div>

          {/* Phase 3A — Périmètre géographique métier (distinct du pays de rattachement) */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="org-geo-scope">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Périmètre géographique</p>
            <p className="mt-1 text-xs text-slate-500">
              Le pays définit le rattachement général de l'organisation. Le périmètre géographique définit la
              destination réellement représentée et les données principales utilisées par Lodge.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Niveau</label>
                {canManage ? (
                  <select
                    value={profile.geographic_level}
                    onChange={(e) => setProfile((p) => ({ ...p, geographic_level: e.target.value }))}
                    data-testid="profile-geo-level"
                    className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
                  >
                    <option value="">— Non défini —</option>
                    <option value="COUNTRY">Pays (COUNTRY)</option>
                    <option value="REGION">Région (REGION)</option>
                    <option value="CITY">Ville (CITY)</option>
                    <option value="DISTRICT">District (DISTRICT)</option>
                    <option value="NEIGHBORHOOD">Quartier (NEIGHBORHOOD)</option>
                    <option value="STREET">Rue (STREET)</option>
                    <option value="DESTINATION">Destination / Zone (DESTINATION)</option>
                    <option value="PROPERTY">Établissement (PROPERTY)</option>
                  </select>
                ) : (
                  <p className="text-sm text-slate-700" data-testid="profile-geo-level-ro">{profile.geographic_level || "—"}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Entité</label>
                {canManage ? (
                  <input
                    value={profile.geographic_entity}
                    onChange={(e) => setProfile((p) => ({ ...p, geographic_entity: e.target.value }))}
                    data-testid="profile-geo-entity"
                    placeholder="ex. France, Paris, Île-de-France"
                    className="h-11 w-full rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
                  />
                ) : (
                  <p className="text-sm text-slate-700">{profile.geographic_entity || "—"}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Code (optionnel)</label>
                {canManage ? (
                  <input
                    value={profile.geographic_code}
                    onChange={(e) => setProfile((p) => ({ ...p, geographic_code: e.target.value }))}
                    data-testid="profile-geo-code"
                    placeholder="ex. FR"
                    className="h-11 w-full rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
                  />
                ) : (
                  <p className="text-sm text-slate-700">{profile.geographic_code || "—"}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Principaux marchés</label>
            <p className="text-xs text-slate-500">
              Marchés émetteurs à suivre. Utilisez « Ajouter un marché » pour rechercher n'importe quel pays.
            </p>
            <MarketSelect
              value={profile.source_markets}
              onChange={(next) => setProfile((p) => ({ ...p, source_markets: next }))}
              canManage={canManage}
            />
          </div>

          <div className="space-y-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Périodes prioritaires</label>
              <span className="text-xs font-medium text-slate-400" data-testid="periods-counter">
                {profile.priority_periods.length} mois sélectionné{profile.priority_periods.length > 1 ? "s" : ""}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Indiquez les mois clés de votre activité (haute saison, périodes à développer). Ces repères aident à
              contextualiser votre organisation ; ils n'ont pour l'instant aucun effet sur les calculs.
            </p>
            {canManage && (
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="periods-select-all"
                  onClick={() => setProfile((p) => ({ ...p, priority_periods: [...PERIODS] }))}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Tout sélectionner
                </button>
                <button
                  type="button"
                  data-testid="periods-clear-all"
                  onClick={() => setProfile((p) => ({ ...p, priority_periods: [] }))}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Tout désélectionner
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {PERIODS.map((p) => (
                <Chip
                  key={p}
                  active={profile.priority_periods.includes(p)}
                  onClick={() => canManage && toggleProfile("priority_periods", p)}
                  testid={`profile-period-${p}`}
                >
                  {p}
                </Chip>
              ))}
            </div>
          </div>

          <div className="space-y-2.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Objectifs</label>
            <div className="flex flex-wrap gap-2">
              {OBJECTIVES.map((o) => (
                <Chip
                  key={o}
                  active={profile.objectives.includes(o)}
                  onClick={() => canManage && toggleProfile("objectives", o)}
                  testid={`profile-objective-${o}`}
                >
                  {objectiveLabel(o)}
                </Chip>
              ))}
            </div>
          </div>

          {canManage && (
            <button
              type="submit"
              disabled={savingProfile}
              data-testid="profile-save-org"
              className="flex h-11 items-center gap-2 rounded-lg bg-[#1E3D2B] px-6 text-sm font-semibold text-white hover:bg-[#2C5540] disabled:opacity-60"
            >
              <Save size={16} /> {savingProfile ? "Enregistrement…" : "Enregistrer le profil"}
            </button>
          )}
        </form>
      </section>

      {/* Billing & subscription (Phase DEMO-PROD.2D.2C.1) */}
      {activeOrgId ? (
        <BillingPanel
          orgId={activeOrgId}
          role={activeMembership?.role}
          isDemo={Boolean(org?.is_demo) || readOnly}
        />
      ) : null}

      {/* Members */}
      <section className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="members-section">
        <h2 className="font-heading text-lg font-semibold text-[#1E3D2B]">Membres</h2>
        <div className="mt-6 overflow-hidden rounded-xl border border-slate-200">
          <SelectableTable rows={members} rowId={(m) => m.membership_id} filename="settings.csv" ariaLabel="Tableau — sélection et défilement">
            <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><th className="px-2 py-2"><SelectAllHeaderCheckbox /></th>
                <th className="px-5 py-3">Utilisateur</th>
                <th className="px-5 py-3">E-mail</th>
                <th className="px-5 py-3">Rôle</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.membership_id} className="border-b border-slate-100 last:border-0" data-testid={`member-${m.user.email}`}><td className="px-2 py-2"><RowCheckbox id={m.membership_id} /></td>
                  <td className="px-5 py-3 text-sm font-medium text-[#1E3D2B]">{m.user.name}</td>
                  <td className="px-5 py-3 text-sm text-slate-500">{m.user.email}</td>
                  <td className="px-5 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${roleStyle[m.role]}`}>
                      {m.role}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </SelectableTable>
        </div>
      </section>

      {/* Invite */}
      {canManage && (
        <section className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="invite-section">
          <div className="flex items-center gap-2">
            <UserPlus size={18} className="text-[#3E7256]" />
            <h2 className="font-heading text-lg font-semibold text-[#1E3D2B]">Inviter un utilisateur</h2>
          </div>
          <form onSubmit={invite} className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end" data-testid="invite-form">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">E-mail</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email"
                placeholder="collegue@organisation.com"
                required
                className="h-11 w-full rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
              />
            </div>
            <div className="space-y-1.5 sm:w-48">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rôle</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role-trigger" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`invite-role-${r}`}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              type="submit"
              data-testid="invite-submit"
              className="flex h-11 items-center justify-center rounded-lg bg-[#1E3D2B] px-5 text-sm font-semibold text-white hover:bg-[#2C5540]"
            >
              Inviter
            </button>
          </form>

          {newLink && (
            <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="invite-link-box">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Lien d'invitation (mode démo)
              </p>
              <CopyField value={newLink} testid="invite-link" />
            </div>
          )}

          {invitations.length > 0 && (
            <div className="mt-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Invitations en attente
              </p>
              <div className="space-y-3">
                {invitations.map((inv) => (
                  <div key={inv.id} className="rounded-lg border border-slate-200 p-3" data-testid={`pending-invite-${inv.email}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">{inv.email}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${roleStyle[inv.role]}`}>
                        {inv.role}
                      </span>
                    </div>
                    <CopyField value={inv.invite_link} testid={`invite-link-${inv.email}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Org-scoped data (isolation) */}
      <section className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="records-section">
        <h2 className="font-heading text-lg font-semibold text-[#1E3D2B]">Données de l'organisation</h2>
        <p className="mt-1 text-xs text-slate-400">
          Ces données sont privées à {activeMembership?.org.name} et invisibles pour toute autre organisation.
        </p>
        {canWrite && (
          <form onSubmit={addRecord} className="mt-5 flex gap-3" data-testid="record-form">
            <input
              value={newRecord}
              onChange={(e) => setNewRecord(e.target.value)}
              data-testid="record-title"
              placeholder="Nouvelle donnée / note privée"
              required
              className="h-11 flex-1 rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
            />
            <button
              type="submit"
              data-testid="record-submit"
              className="flex h-11 items-center gap-1.5 rounded-lg bg-[#3E7256] px-4 text-sm font-semibold text-white hover:bg-[#2F5A43]"
            >
              <Plus size={16} /> Ajouter
            </button>
          </form>
        )}
        <ul className="mt-6 divide-y divide-slate-100" data-testid="records-list">
          {records.map((r) => (
            <li key={r.id} className="flex items-center gap-3 py-3 first:pt-0" data-testid={`record-${r.id}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-[#3E7256]" />
              <span className="text-sm text-slate-700">{r.title}</span>
            </li>
          ))}
          {records.length === 0 && <li className="py-3 text-sm text-slate-400">Aucune donnée pour le moment.</li>}
        </ul>
      </section>
    </div>
  );
}
